import { Link } from "react-router-dom";
import type { Article } from "@/mocks/news";

type ArticleCardProps = {
  article: Article;
  variant?: "default" | "compact" | "horizontal";
  showExcerpt?: boolean;
};

export default function ArticleCard({
  article,
  variant = "default",
  showExcerpt = true,
}: ArticleCardProps) {
  if (variant === "compact") {
    return (
      <Link
        to={`/article/${article.id}`}
        className="group flex items-start gap-3 py-3"
        title={`${article.title} - 美日吃瓜`}
      >
        <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-accent-100 text-xs font-bold text-accent-800">
          <i className="ri-fire-line" aria-hidden="true" />
        </span>
        <span className="min-w-0">
          <span className="block text-sm font-medium leading-relaxed text-foreground-800 transition-colors group-hover:text-primary-700">
            {article.title}
          </span>
          <span className="mt-1 flex items-center gap-2 text-xs text-foreground-500">
            <span>{article.categoryName}</span>
            <span className="h-1 w-1 rounded-full bg-foreground-300" aria-hidden="true" />
            <span>{article.views}人看过</span>
          </span>
        </span>
      </Link>
    );
  }

  if (variant === "horizontal") {
    return (
      <Link
        to={`/article/${article.id}`}
        className="group flex flex-col gap-4 overflow-hidden rounded-lg border border-background-200 bg-background-50 p-3 transition-colors hover:border-primary-300 sm:flex-row"
        title={`${article.title} - 美日吃瓜`}
      >
        <div className="h-[168px] w-full shrink-0 overflow-hidden rounded-md bg-background-200 sm:h-[120px] sm:w-[190px]">
          <img
            src={article.cover}
            alt={`${article.title} 配图`}
            title={`${article.title} - 美日吃瓜`}
            className="h-full w-full object-top transition-transform duration-500 group-hover:scale-105"
          />
        </div>
        <div className="flex min-w-0 flex-col justify-center py-1">
          <span className="text-xs font-medium text-primary-600">{article.categoryName}</span>
          <h3 className="mt-1.5 font-heading text-base font-bold leading-snug text-foreground-900 transition-colors group-hover:text-primary-700">
            {article.title}
          </h3>
          {showExcerpt ? (
            <p className="mt-1.5 line-clamp-2 hidden text-sm leading-relaxed text-foreground-600 sm:block">
              {article.excerpt}
            </p>
          ) : null}
          <span className="mt-2 flex items-center gap-3 text-xs text-foreground-500">
            <span>{article.date}</span>
            <span>{article.readTime} 分钟阅读</span>
          </span>
        </div>
      </Link>
    );
  }

  return (
    <article className="group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300">
      <Link to={`/article/${article.id}`} className="block" title={`${article.title} - 美日吃瓜`}>
        <div className="relative h-[200px] w-full overflow-hidden bg-background-200">
          <img
            src={article.cover}
            alt={`${article.title} 配图`}
            title={`${article.title} - 美日吃瓜`}
            className="h-full w-full object-top transition-transform duration-700 group-hover:scale-105"
          />
          <span className="absolute left-3 top-3 rounded-md bg-primary-500 px-2.5 py-1 text-xs font-medium text-background-50">
            {article.categoryName}
          </span>
        </div>
      </Link>
      <div className="flex flex-1 flex-col p-4">
        <Link to={`/article/${article.id}`} title={`${article.title} - 美日吃瓜`}>
          <h3 className="font-heading text-base font-bold leading-snug text-foreground-900 transition-colors group-hover:text-primary-700 md:text-lg">
            {article.title}
          </h3>
        </Link>
        {showExcerpt ? (
          <p className="mt-2 line-clamp-3 flex-1 text-sm leading-relaxed text-foreground-600">
            {article.excerpt}
          </p>
        ) : null}
        <div className="mt-4 flex items-center justify-between border-t border-background-200 pt-3 text-xs text-foreground-500">
          <span className="flex items-center gap-1.5">
            <i className="ri-user-line" aria-hidden="true" />
            {article.author}
          </span>
          <span className="flex items-center gap-1.5">
            <i className="ri-eye-line" aria-hidden="true" />
            {article.views}
          </span>
        </div>
      </div>
    </article>
  );
}